import {
  Badge,
  Box,
  Flex,
  Grid,
  H2,
  H3,
  Message,
  ProgressCircle,
  Select,
  Small,
  Table,
  Text,
} from '@bigcommerce/big-design';
import { useEffect, useState } from 'react';

import { SetupChecklist } from '@/components/SetupChecklist';
import { api } from '@/services/api';
import type { StoreInfo } from '@/services/api/types';
import type { DashboardStats, Order, SessionContext } from '@/types';
import { getUserFriendlyMessage } from '@/utils/errors';

interface Channel {
  id: number;
  name: string;
  type: string;
  isEnabled: boolean;
}

interface StatCardProps {
  value: string | number;
  label: string;
}

function StatCard({ value, label }: StatCardProps) {
  return (
    <Box
      backgroundColor="white"
      padding="large"
      borderRadius="normal"
      shadow="raised"
    >
      <Flex flexDirection="column" alignItems="center">
        <Text
          as="span"
          bold
          color="primary"
          style={{ fontSize: '2rem' }}
        >
          {value}
        </Text>
        <Text as="span" color="secondary60" marginTop="xSmall">
          {label}
        </Text>
      </Flex>
    </Box>
  );
}

interface DashboardPageProps {
  session: SessionContext;
  onNavigate?: (tabId: string) => void;
}

export function DashboardPage({ session, onNavigate }: DashboardPageProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [storeInfo, setStoreInfo] = useState<StoreInfo | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<string>(session.channelId.toString());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadInitialData() {
      try {
        const [channelsResult, storeInfoResult] = await Promise.all([
          api.getChannels(),
          api.getStoreInfo().catch(() => null),
        ]);
        setChannels(channelsResult.channels.filter((c: Channel) => c.isEnabled));
        setStoreInfo(storeInfoResult);
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    }
    loadInitialData();
  }, []);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel]);

  async function loadDashboardData() {
    try {
      setIsLoading(true);
      setError(null);

      const channelId = selectedChannel === 'all' ? undefined : selectedChannel;

      // Fetch real data from API
      const [statsResponse, ordersResponse] = await Promise.all([
        api.getDashboardStats({ channelId }),
        api.getRecentOrders({ channelId }),
      ]);

      setStats(statsResponse);
      setOrders(ordersResponse.orders);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(getUserFriendlyMessage(err));
    } finally {
      setIsLoading(false);
    }
  }

  const channelOptions = [
    { value: 'all', content: 'All Channels' },
    ...channels.map(c => ({
      value: c.id.toString(),
      content: `${c.name}${c.id === session.channelId ? ' (Default)' : ''}`,
    })),
  ];

  function formatCurrency(amount: number, currency: string) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
    }).format(amount);
  }

  function formatTime(isoString: string) {
    return new Date(isoString).toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (isLoading) {
    return (
      <Box padding="large">
        <Flex justifyContent="center">
          <ProgressCircle size="large" />
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="large">
        <Message
          type="error"
          header="Error"
          messages={[{ text: error }]}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Channel Filter */}
      <Flex justifyContent="space-between" alignItems="center" marginBottom="large">
        <H2 marginBottom="none">Dashboard</H2>
        <Box style={{ width: '200px' }}>
          <Select
            options={channelOptions}
            value={selectedChannel}
            onOptionChange={setSelectedChannel}
            placeholder="Select channel"
          />
        </Box>
      </Flex>

      {/* Store Information Panel */}
      {storeInfo && (
        <Box
          backgroundColor="white"
          padding="medium"
          borderRadius="normal"
          shadow="raised"
          marginBottom="large"
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Flex alignItems="center">
              {storeInfo.logo && (
                <Box marginRight="medium">
                  <img
                    src={storeInfo.logo}
                    alt={storeInfo.name}
                    style={{ maxHeight: '40px', maxWidth: '120px', objectFit: 'contain' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </Box>
              )}
              <Box>
                <Flex alignItems="center">
                  <Text bold style={{ fontSize: '1.1rem' }}>{storeInfo.name}</Text>
                  <Badge
                    variant={storeInfo.status === 'live' ? 'success' : 'secondary'}
                    label={storeInfo.status}
                    marginLeft="small"
                  />
                </Flex>
                <Small color="secondary60">
                  {storeInfo.domain} | {storeInfo.country}
                </Small>
              </Box>
            </Flex>
            <Box style={{ textAlign: 'right' }}>
              <Small color="secondary60">Store Hash</Small>
              <Text bold>{session.storeHash}</Text>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Setup Checklist - shows until setup is complete */}
      {onNavigate && <SetupChecklist onNavigate={onNavigate} />}

      {/* Stats Cards */}
      <Grid gridColumns="repeat(3, 1fr)" gridGap="large" marginBottom="xLarge">
        <StatCard
          value={stats?.ordersToday ?? 0}
          label="Orders Today"
        />
        <StatCard
          value={formatCurrency(stats?.revenueToday ?? 0, stats?.currency ?? 'AUD')}
          label="Revenue Today"
        />
        <StatCard
          value={stats?.activeDevices ?? 0}
          label="Active Devices"
        />
      </Grid>

      {/* Recent Orders */}
      <Box
        backgroundColor="white"
        padding="large"
        borderRadius="normal"
        shadow="raised"
      >
        <H3 marginBottom="medium">Recent POS Orders</H3>

        {orders.length === 0 ? (
          <Box padding="large" style={{ textAlign: 'center' }}>
            <Text color="secondary60">No orders yet today</Text>
          </Box>
        ) : (
          <Table
            columns={[
              {
                header: 'Order #',
                hash: 'orderNumber',
                render: ({ orderNumber }) => <Text bold>{orderNumber}</Text>,
              },
              {
                header: 'Time',
                hash: 'time',
                render: ({ createdAt }) => <Text>{formatTime(createdAt)}</Text>,
              },
              {
                header: 'Items',
                hash: 'itemCount',
                render: ({ itemCount }) => <Text>{itemCount}</Text>,
              },
              {
                header: 'Total',
                hash: 'total',
                render: ({ totalIncTax, currency }) => (
                  <Text bold>{formatCurrency(totalIncTax, currency)}</Text>
                ),
              },
              {
                header: 'Staff',
                hash: 'staff',
                render: ({ staffName }) => <Text>{staffName}</Text>,
              },
              {
                header: 'Status',
                hash: 'status',
                render: ({ status }) => (
                  <Badge
                    variant={status === 'Completed' ? 'success' : 'secondary'}
                    label={status}
                  />
                ),
              },
            ]}
            items={orders}
            itemName="orders"
            stickyHeader
          />
        )}
      </Box>
    </Box>
  );
}
